import { Component, OnInit, OnDestroy, Input} from '@angular/core';
import { Router, NavigationStart } from '@angular/router';
import { Subscription, Observable, combineLatest, of, forkJoin } from "rxjs";
import { tap, map, filter, mergeMap } from "rxjs/operators";
import { GridOptions, ColDef, GridApi } from "ag-grid-community";
import { ModuleGroupService } from "../../../../common/services/modulegroup.service";
import { Module as LunaModule } from "../../../../common/models/module.model";
import { NavigationReference } from "../../../../common/models/navigationreference.model";
import { FlowOnBehalfService } from "../../../../flow/services/flow-on-behalf.service";
import { GridFormatterService } from "../../../../common/services/grid-formatter.service";
import { AuthService } from "../../../../services/auth.service";
import { LunaClaimValues } from "../../../../models/user-identity.model";
import { FlowIndex } from "../../../../common/models/flow.dto";
import { dateComparator } from "../../../../common/components/data-table/comparators/data-table.comparators";
import { RouterLinkRendererComponent } from "../../../../common/components/ag-grid/router-link-renderer.component";
import { IconActionRendererComponent } from "../../../../common/components/ag-grid/icon-action-renderer.component";
import { TabComponent } from "../../../../common/components/tabs/tab.component";
import { FlowIndexSpec, FlowInstanceService } from "../../../../flow/services/flow-instance.service";
import { ModuleGroup } from "../../../../common/models/modulegroup.model";
import { DynamicDialogService } from "../../../../core/services/dynamic-dialog.service";
import { Dialog, DialogButton } from "../../../../core/models/dialog.model";

import { DatePipe } from '@angular/common';
import { GroupService, GroupSearchSpec } from "../../../../admin/services/group.service";
import { Group } from "../../../../admin/models/group.model";
import { ContactStub, Contact } from "../../../../admin/models/contact.model";
import { PendingFlow } from '../../../../flow/models/flow.model';
import { WorkOrderService } from '../../../../common/services/work-order.service';
import { UserIdentity } from '../../../../models/user-identity.model';
import { OrganizationStub } from '../../../../common/models/organization.model';
import { WorkOrderType, PendingWorkOrderAssignment } from '../../../../common/models/work-order.model';
import { DateService } from '../../../../common/services/date.service';
import { ViewBase } from '../view-base';



@Component({
    selector: 'paper-submissions-widget',
    templateUrl: './paper-submissions-widget.component.html',
    styleUrls: ['./paper-submissions-widget.component.scss', '../work-order-widget.component.scss'],
    standalone: false
})
export class PaperSubmissionsWidgetComponent extends ViewBase implements OnInit, OnDestroy {
    @Input() widgetTitle: string;


    private _createdDateField = 'createdDate';
    allUsers: ContactStub[] = [];
    groups: Group[] = [];
    notSubmittedFilteredUsers: ContactStub[] = [];
    returnedFilteredUsers: ContactStub[] = [];
    ejectedFilteredUsers: ContactStub[] = [];
    orgStubs: OrganizationStub[];
    notSubmittedData: FlowIndex[];
    returnedData: FlowIndex[];
    ejectedData: FlowIndex[];
    notSubmittedPendingFlows: PendingFlow[];
    returnedPendingFlows: PendingFlow[];
    ejectedPendingFlows: PendingFlow[];
    subscriptions: Subscription = new Subscription();
    selectedItem: any;
    showingDetail: boolean = false;


    columnApplicationNoClick: ColDef = {
        headerName: 'Application #',
        headerTooltip: 'Application Number',
        headerClass: "wrapped",
        field: 'flowIdentifier',
        filter: 'agTextColumnFilter',
        filterParams: this.gridFormatter.defaultTextFilterParams,
        maxWidth: 160,
        tooltipField: 'flowIdentifier'
    } as ColDef;

    columnApplicationNumber: ColDef = {
        cellRenderer: RouterLinkRendererComponent,
        cellRendererParams: {
            onBehalfIdField: "flowId",
            textField: 'flowIdentifier',
            tooltipField: 'flowIdentifier'
        },
        headerName: 'Application #',
        headerTooltip: 'Application Number',
        headerClass: "wrapped",
        tooltipField: 'flowIdentifier',
        field: 'flowIdentifier',
        filter: 'agTextColumnFilter',
        filterParams: this.gridFormatter.defaultTextFilterParams,
        maxWidth: 160
    } as ColDef;

    columnApplicationNumberIncomplete: ColDef = {
        cellRenderer: RouterLinkRendererComponent,
        cellRendererParams: {
            textField: 'flowIdentifier',
            tooltipField: 'flowIdentifier',
            openInNewWindow: false,
            url: (data: FlowIndex) => { return `/flow/review-on-behalf/${data.flowId}`; }
        },
        headerName: 'Application #',
        headerTooltip: 'Application Number',
        headerClass: "wrapped",
        tooltipField: 'flowIdentifier',
        field: 'flowIdentifier',
        filter: 'agTextColumnFilter',
        filterParams: this.gridFormatter.defaultTextFilterParams,
        maxWidth: 160
    } as ColDef;

    columnResumeEjected: ColDef = {
        cellRenderer: IconActionRendererComponent,
        cellRendererParams: {
            buttons: [
                {
                    action: (obj: any) => this.startResume(obj),
                    icon: "edit"
                }
            ]
        },
        headerName: "Resume Entry",
        headerTooltip: "Resume Entry",
        headerClass: "wrapped",
        maxWidth: 125,
        pinned: "left"
    } as ColDef;

    columnApplicationType: ColDef = {
        headerName: "Application Type",
        headerTooltip: "Application Type",
        headerClass: "wrapped",
        field: "name",
        filter: 'agTextColumnFilter',
        filterParams: this.gridFormatter.defaultTextFilterParams,
        tooltipField: "name",
        maxWidth: 200
    } as ColDef;

    columnCompanyName: ColDef = {
        headerName: 'Company',
        headerTooltip: 'Company',
        headerClass: "wrapped",
        field: 'companyName',
        filter: 'agTextColumnFilter',
        filterParams: this.gridFormatter.defaultTextFilterParams,
        tooltipField: 'companyName',
        maxWidth: 150
    } as ColDef;

    columnDateCreated: ColDef = {
        headerName: 'Created',
        headerTooltip: 'Created',
        headerClass: "wrapped",
        tooltipValueGetter: this.gridFormatter.formatDate(this._createdDateField),
        field: this._createdDateField,
        valueFormatter: this.gridFormatter.formatDate(this._createdDateField),
        comparator: dateComparator,
        filter: "agDateColumnFilter",
        filterParams: this.gridFormatter.defaultDateFilterParams,
        minWidth: 125,
        maxWidth: 130
    } as ColDef;

    columnRecorder: ColDef = {
        headerName: "Recorder",
        headerTooltip: "Recorder",
        headerClass: "wrapped",
        field: "userName",
        tooltipField: "userName",
        filter: "agTextColumnFilter",
        filterParams: this.gridFormatter.defaultTextFilterParams,
        maxWidth: 150
    } as ColDef;

    columnState: ColDef = {
        headerName: "Current State",
        headerTooltip: 'Current State',
        headerClass: "wrapped",
        field: 'state',
        filter: 'agTextColumnFilter',
        filterParams: this.gridFormatter.defaultTextFilterParams,
        maxWidth: 150,
        tooltipField: 'state'
    }

    wellNameColumn: ColDef = {
        editable: false,
        headerName: "Well Name",
        headerTooltip: "Well Name",
        valueGetter: this.gridFormatter.formatPotentiallyBlankField("wellName", "N/A"),
        tooltipValueGetter: this.gridFormatter.formatPotentiallyBlankField("wellName", "N/A"),
        minWidth: 125,
        maxWidth: 200,
        filter: "agTextColumnFilter",
        resizable: true
    } as ColDef;


    wellNumberColumn: ColDef = {
        editable: false,
        headerName: "Well Number",
        headerTooltip: "Well Number",
        valueGetter: this.gridFormatter.formatPotentiallyBlankField("wellNumber", "N/A"),
        tooltipValueGetter: this.gridFormatter.formatPotentiallyBlankField("wellNumber", "N/A"),
        minWidth: 125,
        maxWidth: 200,
        filter: "agTextColumnFilter",
		resizable: true
    } as ColDef;

    incompleteColumns: ColDef[] = [
        this.columnResumeEjected,
        this.columnDateCreated,
        this.columnRecorder,
        this.columnCompanyName,
        this.columnApplicationNumberIncomplete,
        this.columnApplicationType,
        this.columnState
    ];

    buildIncompleteColumns(): ColDef[] {
        let colDefs: ColDef[] = [];
        colDefs.push(this.columnResumeEjected);
        colDefs.push(this.columnDateCreated);
        colDefs.push(this.columnRecorder);
        colDefs.push(this.columnCompanyName);
        colDefs.push(this.wellNameColumn);
        colDefs.push(this.wellNumberColumn);
        colDefs.push(this.columnApplicationNumberIncomplete);
        colDefs.push(this.columnApplicationType);
        colDefs.push({
            headerName: "Assignee",
            headerTooltip: "Assignee",
            field: "assignedUserId",
            suppressFiltersToolPanel: true,
            suppressMovable: true,
            editable: true,
            cellEditor: 'agRichSelectCellEditor',
            cellEditorPopup: true,
            cellEditorParams: {
                formatValue: (value: any) => value?.displayName ?? 'Unassigned',
                values: this.ejectedFilteredUsers
            },
            cellRenderer: this.renderAssignedUser,
            valueGetter: (params) => this.allUsers.find(u => u.id === params.data?.assignedUserId) ?? null,
            filterValueGetter: (params) => this.allUsers.find(u => u.id === params.data?.assignedUserId)?.displayName ?? 'Unassigned',
            valueSetter: this.assign,
            tooltipValueGetter: this.renderAssignedUser,
            maxWidth: 125,
            onCellValueChanged: this.cellValueChanged
        } as ColDef);
        colDefs.push(this.columnState);

        this.gridFormatter.applyFilterKeyboardHintTooltips(colDefs);
        return colDefs;
    }

	incompleteGridOptions: GridOptions;
	incompleteGridApi?: GridApi;

    incompletes: FlowIndex[];


    indicies: FlowIndex[];
    loadingOverlay: string = '<span class="ag-overlay-loading-center">Loading...</span>';

    notSubmittedColumns: ColDef[] = [
        this.columnDateCreated,
        this.columnRecorder,
        this.columnCompanyName,
        this.columnApplicationNumber,
        this.columnApplicationType,
        this.columnState
    ];

	notSubmittedGridOptions: GridOptions;
	notSubmittedGridApi?: GridApi;

    buildNotSubmittedColumns(): ColDef[] {
        let colDefs: ColDef[] = [];
        colDefs.push(this.columnDateCreated);
        colDefs.push(this.columnRecorder);
        colDefs.push(this.columnCompanyName);
        colDefs.push(this.wellNameColumn);
        colDefs.push(this.wellNumberColumn);
        colDefs.push(this.columnApplicationNumber);
        colDefs.push(this.columnApplicationType);
        colDefs.push({
            headerName: "Assignee",
            headerTooltip: "Assignee",
            field: "assignedUserId",
            suppressFiltersToolPanel: true,
            suppressMovable: true,
            editable: true,
            cellEditor: 'agRichSelectCellEditor',
            cellEditorPopup: true,
            cellEditorParams: {
                formatValue: (value: any) => value?.displayName ?? 'Unassigned',
                values: this.notSubmittedFilteredUsers
            },
            cellRenderer: this.renderAssignedUser,
            valueGetter: (params) => this.allUsers.find(u => u.id === params.data?.assignedUserId) ?? null,
            filterValueGetter: (params) => this.allUsers.find(u => u.id === params.data?.assignedUserId)?.displayName ?? 'Unassigned',
            valueSetter: this.assign,
            tooltipValueGetter: this.renderAssignedUser,
            maxWidth: 125,
            onCellValueChanged: this.cellValueChanged
        } as ColDef);
        colDefs.push(this.columnState);

        this.gridFormatter.applyFilterKeyboardHintTooltips(colDefs);
        return colDefs;
    }

    resumeDialog: Dialog = new Dialog("This application was ejected and correspondence generated for the applying organization.  Any changes to the application will end its ejected status and change it to either Not Submitted or Returned, depending on its state at the time it was ejected.  Do you wish to continue?", [new DialogButton("Cancel", false, () => { this.cancelResume(); }), new DialogButton("OK", true, () => { this.resumeApplication(); })]);

    resumingApplication?: FlowIndex | null;

    returned: FlowIndex[];

    returnedColumns: ColDef[] = [
        this.columnDateCreated,
        this.columnRecorder,
        this.columnCompanyName,
        this.wellNameColumn,
        this.wellNumberColumn,
        this.columnApplicationNumber,
        this.columnApplicationType,
        this.columnState
    ];

	returnedGridOptions: GridOptions;
	returnedGridApi?: GridApi;

    buildReturnColumns(): ColDef[] {
        let colDefs: ColDef[] = [];
        colDefs.push(this.columnDateCreated);
        colDefs.push(this.columnRecorder);
        colDefs.push(this.columnCompanyName);
        colDefs.push(this.wellNameColumn);
        colDefs.push(this.wellNumberColumn);
        colDefs.push(this.columnApplicationNumber);
        colDefs.push(this.columnApplicationType);
        colDefs.push({
            headerName: "Assignee",
            headerTooltip: "Assignee",
            field: "assignedUserId",
            suppressFiltersToolPanel: true,
            suppressMovable: true,
            editable: true,
            cellEditor: 'agRichSelectCellEditor',
            cellEditorPopup: true,
            cellEditorParams: {
                formatValue: (value: any) => value?.displayName ?? 'Unassigned',
                values: this.returnedFilteredUsers
            },
            cellRenderer: this.renderAssignedUser,
            valueGetter: (params) => this.allUsers.find(u => u.id === params.data?.assignedUserId) ?? null,
            filterValueGetter: (params) => this.allUsers.find(u => u.id === params.data?.assignedUserId)?.displayName ?? 'Unassigned',
            valueSetter: this.assign,
            tooltipValueGetter: this.renderAssignedUser,
            maxWidth: 125,
            onCellValueChanged: this.cellValueChanged
        } as ColDef);
        colDefs.push(this.columnState);

        this.gridFormatter.applyFilterKeyboardHintTooltips(colDefs);
        return colDefs;
    }

    reviewColumns: ColDef[] = [
        this.columnDateCreated,
        this.columnRecorder,
        this.columnCompanyName,
        this.wellNameColumn,
        this.wellNumberColumn,
        this.columnApplicationNoClick,
        this.columnApplicationType,
        this.columnState
    ];

	reviewGridOptions: GridOptions;
	reviewGridApi?: GridApi;

    reviews: FlowIndex[];
    organizationModule: ModuleGroup = undefined;
    submissionsModule: ModuleGroup = undefined;

    subscription: Subscription = new Subscription();

    unsubmitted: FlowIndex[];

    constructor(
        private flowService: FlowOnBehalfService,
        private gridFormatter: GridFormatterService,
        private moduleService: ModuleGroupService,
        private authService: AuthService,
        private instanceService: FlowInstanceService,
        public router: Router,
        public dialogService: DynamicDialogService,
        public workOrderService: WorkOrderService,
        public flowInstanceService: FlowInstanceService,
        public groupService: GroupService
    ) {
        super("ViewPaperSubmissionsWidget", [LunaClaimValues.ViewPaperSubmissionsWidget]);
    }

    ngOnInit() {
        this.setGridOptions();

        const sub$ = forkJoin([
            this.getDataObs$(),
            this.getObservable("Not Submitted")
        ]).pipe(
            map(([[level1Rows, level2Rows, level3Rows], notSubmittedRows]) => {
                const level1Groups = this.groups.filter(g => this.groupHasPermission(g) && level1Rows.map(r => r.assignedGroupId).findIndex(r => r == g.id) != null);
                const level2Groups = this.groups.filter(g => this.groupHasPermission(g) && level2Rows.map(r => r.assignedGroupId).findIndex(r => r == g.id) != null);
                const level3Groups = this.groups.filter(g => this.groupHasPermission(g) && level3Rows.map(r => r.assignedGroupId).findIndex(r => r == g.id) != null);
                this.notSubmittedFilteredUsers = this.buildUserList(level1Groups);
                this.returnedFilteredUsers = this.buildUserList(level2Groups);
                this.ejectedFilteredUsers = this.buildUserList(level3Groups);
                return notSubmittedRows;
            })
        ).subscribe({
            next: (notSubmittedRows: FlowIndex[]) => {
                this.notSubmittedGridApi?.setGridOption("columnDefs", []);
                this.notSubmittedGridApi?.setGridOption("columnDefs", this.buildNotSubmittedColumns());
                this.returnedGridApi?.setGridOption("columnDefs", []);
                this.returnedGridApi?.setGridOption("columnDefs", this.buildReturnColumns());
                this.incompleteGridApi?.setGridOption("columnDefs", []);
                this.incompleteGridApi?.setGridOption("columnDefs", this.buildIncompleteColumns());
                this.unsubmitted = notSubmittedRows;
                this.notSubmittedGridApi?.setGridOption("loading", false);
                this.notSubmittedGridApi?.setGridOption("rowData", notSubmittedRows);
                this.returnedGridApi?.autoSizeAllColumns(false);
                this.incompleteGridApi?.autoSizeAllColumns(false);
                this.notSubmittedGridApi?.autoSizeAllColumns(false);
            },
            error: (error) => { console.error(error); }
        });
        this.subscriptions.add(sub$);

        this.router.events.pipe(filter(e => e instanceof NavigationStart)).subscribe((routerEvent: NavigationStart) => {
            this.subscriptions.unsubscribe();
        });
    }

    ngOnDestroy() {
        if (this.subscription) this.subscription.unsubscribe();
    }

    assign(params: any): any {
        let result: boolean = false;
        if (params && params.newValue && params.newValue.id) {
            if (params.newValue.id != "-1") {
                params.data.assignedUserId = params.newValue.id;
            } else {
                params.data.assignedUserId = "";
            }

            if (params.context && params.context.componentParent) {
                let parent = <PaperSubmissionsWidgetComponent>params.context.componentParent;
                let selectedItem = parent.allUsers.find(u => u.id == params.newValue.id);
                if (selectedItem) {
                    params.data.assignedUserName = selectedItem.displayName;
                }
            }
            result = true;
        }
        return result;
    }

    cellValueChanged(params: any): void {
        if (params.api) {
            params.api.refreshCells();
        }
    }

    getDataObs$(): Observable<[PendingFlow[], PendingFlow[], PendingFlow[]]> {
        this.showLoadingOverlays(true);

        let allUserSearchObs$ = this.groupService.get({ internal: true, stubs: false, fullContactDetails: true } as GroupSearchSpec);
        let notSubmittedItemSearchObs$ = this.workOrderService.getPendingFlowsByTargetTransition("AllPermitLevel1WorkOrders").pipe(tap((data: PendingFlow[]) => {
            this.notSubmittedPendingFlows = data;
        }));
        let returnedItemSearchObs$ = this.workOrderService.getPendingFlowsByTargetTransition("AllPermitLevel2WorkOrders").pipe(tap((data: PendingFlow[]) => {
            this.returnedPendingFlows = data;
        }));
        let incompleteItemSearchObs$ = this.workOrderService.getPendingFlowsByTargetTransition("AllPermitLevel3WorkOrders").pipe(tap((data: PendingFlow[]) => {
            this.ejectedPendingFlows = data;
        }));

        let level1Subs$ = allUserSearchObs$.pipe(mergeMap((groupData) => {
            this.allUsers = [];
            this.notSubmittedFilteredUsers = [];
            this.returnedFilteredUsers = [];
            this.ejectedFilteredUsers = [];
            if (groupData) {
                if (groupData instanceof Array) {
                    this.groups = groupData as Group[];
                    this.groups.forEach(g => this.allUsers = this.allUsers.concat(g.users));
                }
                this.allUsers = this.filterUniqueUsers(this.allUsers);
                this.allUsers.unshift({ id: "-1", displayName: "Unassigned" } as ContactStub);
            }
            return forkJoin([notSubmittedItemSearchObs$, returnedItemSearchObs$, incompleteItemSearchObs$]);
        }));
        return level1Subs$;
    }

    buildUserList(groups: Group[]): ContactStub[] {
        let userList: ContactStub[] = [];
        groups.forEach(u => userList = userList.concat(u.users));
        userList = this.filterUniqueUsers(userList);
        userList = this.sortUsers(userList);
        userList.unshift({ id: "-1", displayName: "Unassigned" } as ContactStub);
        return userList;
    }

    filterUniqueUsers(users: ContactStub[]): ContactStub[] {
        return users.filter((user, i, arr) => arr.findIndex(t => t.id === user.id) === i);
    }


    renderUserDropdownOptions(params: any): string {
        let returnValue = params && params.value && params.value.displayName ? params.value.displayName : "Unassigned";
        return returnValue;
    }

    renderAssignedUser(params: any): string {
        let returnValue: string = "Unassigned";
        if (params && params.data && params.data.assignedUserId && params.data.assignedUserName) {
            returnValue = params.data.assignedUserName;
        }
        return returnValue;
    }

    saveAssignments(event: any): void {
        const row = event.data as FlowIndex;
        if (!row?.flowId) return;
        if (row.currentWorkItemId) {
            const pending = new PendingWorkOrderAssignment(row.currentWorkItemId, row.assignedUserId, row.assignedUserName, true, row.flowId);
            const sub$ = this.workOrderService.assignWorkOrder(pending).subscribe({
                next: () => { },
                error: (error: any) => { console.error(error); }
            });
            this.subscriptions.add(sub$);
        } else {
            const userId = row.assignedUserId || null;
            const userName = row.assignedUserName || null;
            const sub$ = this.flowInstanceService.assignFlowIndex(row.flowId, userId, userName).subscribe({
                next: () => { },
                error: (error: any) => { console.error(error); }
            });
            this.subscriptions.add(sub$);
        }
    }

    cancelResume(): void {
        this.resumingApplication = null;
    }

    setGridOptions(): void {
        this.notSubmittedGridOptions = <GridOptions>{
            columnDefs: this.buildNotSubmittedColumns(),
            context: {
                componentParent: this
            },
            defaultColDef: { filter: true, resizable: true, sortable: true, editable: false } as ColDef,
            enableBrowserTooltips: false,
            tooltipTrigger: 'focus',
            headerHeight: 50,
            onCellValueChanged: ($event) => this.saveAssignments($event),
            overlayLoadingTemplate: this.loadingOverlay,
            pagination: true,
            paginationAutoPageSize: true,
            suppressHorizontalScroll: false,
			onGridReady: (params) => { this.notSubmittedGridApi = params.api; }
        };

        this.returnedGridOptions = <GridOptions>{
            columnDefs: this.buildReturnColumns(),
            context: {
                componentParent: this
            },
            defaultColDef: { filter: true, resizable: true, sortable: true, editable: false } as ColDef,
            enableBrowserTooltips: false,
            tooltipTrigger: 'focus',
            headerHeight: 50,
            onCellValueChanged: ($event) => this.saveAssignments($event),
            overlayLoadingTemplate: this.loadingOverlay,
            pagination: true,
            paginationAutoPageSize: true,
			suppressHorizontalScroll: false,
			onGridReady: (params) => { this.returnedGridApi = params.api; }
        };
        this.incompleteGridOptions = <GridOptions>{
            columnDefs: this.buildIncompleteColumns(),
            context: {
                componentParent: this
            },
            defaultColDef: { filter: true, resizable: true, sortable: true, editable: false } as ColDef,
            enableBrowserTooltips: false,
            tooltipTrigger: 'focus',
            headerHeight: 50,
            onCellValueChanged: ($event) => this.saveAssignments($event),
            overlayLoadingTemplate: this.loadingOverlay,
            pagination: true,
            paginationAutoPageSize: true,
			suppressHorizontalScroll: false,
			onGridReady: (params) => { this.incompleteGridApi = params.api; }
        };
        this.gridFormatter.applyFilterKeyboardHintTooltips(this.reviewColumns);
        this.reviewGridOptions = <GridOptions>{
            columnDefs: this.reviewColumns,
            defaultColDef: { filter: true, resizable: true, sortable: true, editable: false } as ColDef,
            enableBrowserTooltips: false,
            tooltipTrigger: 'focus',
            headerHeight: 50,
            overlayLoadingTemplate: this.loadingOverlay,
            pagination: true,
            paginationAutoPageSize: true,
			suppressHorizontalScroll: false,
			onGridReady: (params) => { this.reviewGridApi = params.api; }
        };
    }

    showLoadingOverlays(show: boolean): void {
        if (this.notSubmittedGridOptions && this.notSubmittedGridApi) {
            if (show) {
                this.notSubmittedGridApi.setGridOption("rowData",[]);
                this.notSubmittedGridApi.setGridOption("loading", true);
            } else
                this.notSubmittedGridApi.setGridOption("loading", false);
        }

        if (this.returnedGridOptions && this.returnedGridApi) {
            if (show) {
                this.returnedGridApi.setGridOption("rowData",[]);
                this.returnedGridApi.setGridOption("loading", true);
            }
            else
                this.returnedGridApi.setGridOption("loading", false);
        }

        if (this.incompleteGridOptions && this.incompleteGridApi) {
            if (show) {
                this.incompleteGridApi.setGridOption("rowData",[]);
                this.incompleteGridApi.setGridOption("loading", true);
            }
            else
                this.incompleteGridApi.setGridOption("loading", false);
        }
    }

    sortUsers(users: ContactStub[]): ContactStub[] {
        return users.sort((a, b) => {
            if (a.firstName > b.firstName) return 1;
            if (a.firstName < b.firstName) return -1;
            if (a.firstName == b.firstName) {
                if (a.lastName > b.lastName) return 1;
                if (a.lastName < b.lastName) return -1;
            }
            return 0;
        })
    }

    getAllowedFlows(modules?: LunaModule[]): NavigationReference[] {
        if (modules == null) return [];
        let permittedModules: LunaModule[] = modules;
        if (!this.authService.hasSystemPermission(LunaClaimValues.ExecutePermitFlowOnBehalf)) {
            permittedModules = permittedModules.filter(m => m.title?.toLocaleLowerCase().indexOf("permitting") === -1);
        }
        if (!this.authService.hasSystemPermission(LunaClaimValues.ExecuteWellTransferOnBehalf)) {
            permittedModules = permittedModules.filter(m => m.title?.toLocaleLowerCase().indexOf("transfer") === -1);
        }
        if (!this.authService.hasSystemPermission(LunaClaimValues.ExecuteFAFlowOnBehalf)) {
            permittedModules = permittedModules.filter(m => m.title?.toLocaleLowerCase().indexOf("financial assurance") === -1);
        }
        if (!this.authService.hasSystemPermission(LunaClaimValues.ExecuteRegistrationOnBehalf)) {
            permittedModules = permittedModules.filter(m => m.title?.toLocaleLowerCase().indexOf("registration") === -1 || m.title?.toLocaleLowerCase().indexOf("entity name change") != -1);
        }
        let flowTypes: NavigationReference[] = [];
        permittedModules.forEach(m => {
            flowTypes = flowTypes.concat(m.navigationItems ?? []);
        });
        return flowTypes;
    }

    getData(tabName: string): void {
        let obs$ = this.getObservable(tabName);
        this.subscription = obs$.pipe().subscribe(
            (data: FlowIndex[]) => {
                switch (tabName) {
                    case "Not Submitted":
                        this.unsubmitted = data;
                        this.notSubmittedGridApi.setGridOption("loading", false);
                        this.notSubmittedGridApi.setGridOption("rowData", this.unsubmitted);
                        break;
                    case "Returned":
                        this.returned = data;
                        this.returnedGridApi.setGridOption("loading", false);
                        this.returnedGridApi.setGridOption("rowData", this.returned);
                        break;
                    case "Ejected":
                        this.incompletes = data;
                        this.incompleteGridApi.setGridOption("loading", false);
                        this.incompleteGridApi.setGridOption("rowData", this.incompletes);
                        break;
                    case "Submitted":
                        this.reviews = data;
                        this.reviewGridApi.setGridOption("loading", false);
                        this.reviewGridApi.setGridOption("rowData", this.reviews);
                        break;
                }
                this.returnedGridApi.autoSizeAllColumns(false);
                this.incompleteGridApi.autoSizeAllColumns(false);
                this.notSubmittedGridApi.autoSizeAllColumns(false);
            },
            (error) => {
                console.error(error);
            });
    }

    getObservable(tabName: string): Observable<FlowIndex[]> {
        let allowedFlows$: Observable<NavigationReference[]>;
        let index$: Observable<FlowIndex[]> = of([]);
        if (this.submissionsModule && this.organizationModule) {
            allowedFlows$ = of(this.submissionsModule).pipe(map((m) => {
                return this.getAllowedFlows(this.submissionsModule.modules.concat(this.organizationModule.modules));
            }));
        } else {
            allowedFlows$ = this.moduleService.getAll().pipe(map((groups) => {
                this.submissionsModule = groups.find(d => d.title?.toLocaleLowerCase() === "submissions") ??  { modules: [], canDelete: true};
                this.organizationModule = groups.find(d => d.title?.toLocaleLowerCase() === "organization") ?? { modules: [], canDelete: true};
                return this.getAllowedFlows(this.submissionsModule.modules?.concat(this.organizationModule?.modules));
            }));
        }
        let api: GridApi | undefined;
        switch (tabName) {
            case "Not Submitted":
                api = this.notSubmittedGridApi;
                index$ = this.flowService.getOnBehalfFlows("UnsubmittedPaperApplications").pipe(map((flow) => {
                    let filteredFlows = flow.filter(f => f.currentWorkItemId == null || f.currentWorkItemId == "").filter(f => f.ejectedIncomplete == null);
                    return filteredFlows;
                }));
                break;
            case "Returned":
                api = this.returnedGridApi;
                index$ = this.flowService.getOnBehalfFlows("ReturnedPaperApplications").pipe(map((flow) => {
                    let filteredFlows = flow.filter(f => f.currentWorkItemId != null && f.currentWorkItemId != "").filter(f => f.ejectedIncomplete == null);
                    return filteredFlows;
                }))
                break;
            case "Ejected":
                api = this.incompleteGridApi;
                let unsubs$ = this.flowService.getOnBehalfFlows("UnsubmittedPaperApplications");
                let returned$ = this.flowService.getOnBehalfFlows("ReturnedPaperApplications");
                index$ = forkJoin(unsubs$, returned$).pipe(map(([unsubs, returned]) => {
                    let ejectedUnsubs = unsubs.filter(u => u.ejectedIncomplete != null);
                    let ejectedReturns = returned.filter(r => r.ejectedIncomplete != null);
                    return this.unique(ejectedUnsubs.concat(ejectedReturns));
                }));
                break;
            case "Submitted":
                api = this.reviewGridApi;
                let spec = new FlowIndexSpec();
                spec.isOnBehalf = true;
                spec.ownedFlowsOnly = false;
                spec.isInReview = true;
                index$ = this.instanceService.findFlowIndicies(spec);
                break;
        }
        if (api != null) {
            api.setGridOption("loading", true);
        }
        return forkJoin([allowedFlows$, index$]).pipe(map(([modules, indicies]) => {
            return indicies.filter(i => modules.find(m => m.uriComponent == i.flowTemplateId) != null);
        }));
    }

    onTabClicked(tab: TabComponent): void {
        this.getData(tab.title);        
    }

    resumeApplication(): void {
        const url = "/flow/on-behalf";
        this.router.navigate([url, this.resumingApplication.flowId]);
    }

    startResume(obj: FlowIndex): void {
        this.resumingApplication = obj;
        this.resumeDialog.toggleDialog(true);
        this.dialogService.updateDialog(this.resumeDialog);
    }

    unique(data: FlowIndex[]): FlowIndex[] {
        let array = data.sort((a, b) => {
            if (a.flowId < b.flowId) return -1;
            if (b.flowId < a.flowId) return 1;
            return 0;
        });
        for (let i = 0; i < array.length; i++) {
            for (let j = i + 1; j < array.length; j++) {
                if (array[i].flowId === array[j].flowId) {
                    array.splice(j--, 1);
                }
            }
        }
        return array.sort((a, b) => {
            if (a.createdDate < b.createdDate) return -1;
            if (b.createdDate < a.createdDate) return 1;
            if (a.flowIdentifier < b.flowIdentifier) return -1;
            if (b.flowIdentifier < a.flowIdentifier) return 1;
            return 0;
        });
    }
}
